import { FC, ReactNode, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";

import CenteredSpinner from "shared/ui/spinners/centered-spinner";
import { Button } from "shared/ui/shadcn/button";
import { clsxMerge } from "shared/lib/helpers";
import { QueryResultI } from "shared/lib/interfaces/react-query";

interface DataStatesWrapperPropsI {
  viewName?: string; // The name of the view, like "Today's Performance", "Productivity", etc.

  api: QueryResultI<unknown> | undefined;

  loading?: ReactNode;
  loadingClassName?: string;

  error?: ReactNode | ((error: unknown) => ReactNode);
  errorClassName?: string;

  empty?: ReactNode;
  emptyClassName?: string;

  children?: ReactNode | (() => ReactNode);

  /**
   * Opt-in progressive rendering. When the query already holds data:
   *  - a background refetch keeps the children on screen instead of the
   *    `loading` node (TanStack v5 sets isFetching on ANY refetch, including
   *    window-focus refetches), and
   *  - an error arriving on top of good data keeps the children instead of
   *    replacing the whole view with the error card.
   * The caller becomes responsible for telling the viewer that a refresh is
   * in flight or has failed (a small status chip in the panel header).
   * Default false: existing call sites keep their exact behaviour.
   */
  showStaleData?: boolean;
}

/**
 * This component is a wrapper for pending, success, error and empty states
 * for data from API calls / react query. See the example of usage on the user
 * performance dashboard.
 *
 * @component
 * @param viewName - Name of a view, which should be used for error and empty states.
 * @param api - collection of API states and, optionally, refetch function.
 * @param api.isFetching - State: API is fetching data.
 * @param api.isSuccess - State: API has successfully fetched data.
 * @param api.isError - State: API has failed to fetch data.
 * @param api.isEmpty - State: API has successfully fetched data, but it's empty. Usually it's a user defined state.
 * @param api.refetch - Function to re-fetch data from API; the error state offers a Reload button only when it is provided.
 * @param loading - Custom loading state.
 * @param loadingClassName - Custom loading state class name.
 * @param error - Custom error state.
 * @param errorClassName - Custom error state class name.
 * @param empty - Custom empty state.
 * @param emptyClassName - Custom empty state class name.
 * @param children - Children to render when data is successfully fetched.
 * @param showStaleData - Keep rendered children during background refetches and refetch errors (see the prop doc).
 */
export const DataStatesWrapper: FC<DataStatesWrapperPropsI> = ({
  viewName = "data",
  api,
  loading,
  loadingClassName,
  error,
  errorClassName,
  empty,
  emptyClassName,
  children,
  showStaleData = false,
}) => {
  const {
    isFetching = false,
    isSuccess = false,
    isError = false,
    isEmpty = false,
  } = api || {};
  // No noop default: a wrapper with no refetch must not offer a dead button.
  const refetch = api?.refetch;

  // "There is already something worth looking at." Only consulted when the
  // caller opted in: on a background refetch and on a refetch error TanStack
  // keeps the last successful data in `data`, so the children can stay up.
  const hasData = api?.data !== undefined && !isEmpty;
  const shouldKeepChildren = showStaleData && hasData;

  // Error content is reused across both API error and error boundary fallback.
  const errorContent = useMemo(() => {
    const isErrorDefined = error !== undefined;

    return isErrorDefined ? (
      typeof error === "function" ? (
        error(api?.error)
      ) : (
        error
      )
    ) : (
      <div
        className={clsxMerge(
          "flex h-full w-full flex-col items-center justify-center gap-3",
          errorClassName
        )}
      >
        <h4 className="typography-header-8-semibold text-center">
          We’ve detected an error
        </h4>

        <span className="typography-body-4 max-w-[220px] text-center">
          An error occurred while loading {viewName}.
        </span>

        {refetch && <Button onClick={() => refetch()}>Reload</Button>}
      </div>
    );
    // `error` / `errorClassName` were missing here, so a call site that swapped
    // its custom error node kept rendering the stale one.
  }, [viewName, refetch, api?.error, error, errorClassName]);

  if (isError && !shouldKeepChildren) {
    return errorContent;
  }

  if (isFetching && !shouldKeepChildren) {
    if (loading === null) {
      return null;
    }

    return (
      loading || (
        <CenteredSpinner
          className={clsxMerge("h-full w-full", loadingClassName)}
        />
      )
    );
  }

  if (isSuccess || shouldKeepChildren) {
    if (isEmpty) {
      return (
        empty || (
          <div
            className={clsxMerge(
              "flex h-full min-h-[120px] w-full flex-col items-center justify-center",
              "rounded-xl bg-[#F4F4F4] font-normal",
              emptyClassName
            )}
          >
            <span className="text-center text-[#666]">
              No {viewName} data available.
            </span>
          </div>
        )
      );
    }

    return (
      <ErrorBoundary fallback={errorContent}>
        {typeof children === "function" ? children() : children}
      </ErrorBoundary>
    );
  }

  return null;
};
